import {
  UserChangePasswordDto,
  UserLoginDto,
  UserRefreshTokenDto,
  UserSetPasswordDto,
} from '@docbay/schemas';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { UsersActions } from 'applicaiton/constants/actions';
import {
  getToken,
  setUsersRefreshTokenSessionStorage,
} from 'applicaiton/sessionStorage/auth';
import { AxiosError } from 'axios';
import { UsersAPI } from 'integration/api/users';
import { setIsValidCode } from './UsersSlice';
import { authService } from 'common';
import { ForgotPasswordDto } from 'common/auth/forgotPassword/types';

/**
 * Asynchronously logs in a user with the provided login data.
 *
 * @param loginData - The user's login credentials.
 * @returns The response data from the API call, or an error message if the call fails.
 */
export const usersLogin = createAsyncThunk(
  UsersActions.usersLogin,
  async (loginData: UserLoginDto, { rejectWithValue }) => {
    try {
      const { data } = await UsersAPI.usersLogin(loginData);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously refreshes the user's access token with the provided refresh token data.
 *
 * @param refreshTokenData - The user's refresh token credentials.
 * @returns The response data from the API call, or an error message if the call fails.
 */
export const usersRefreshToken = createAsyncThunk(
  UsersActions.usersRefreshToken,
  async (refreshTokenData: UserRefreshTokenDto, { rejectWithValue }) => {
    try {
      const { data } = await UsersAPI.usersRefreshToken(refreshTokenData);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously refreshes the user's expired access token with the provided refresh token data.
 *
 * @param refreshTokenData - The user's refresh token credentials.
 * @returns The response data from the API call, or an error if the call fails.
 */
export const usersRefreshExpiredToken = async (
  refreshTokenData: UserRefreshTokenDto,
) => {
  try {
    const data = await UsersAPI.usersRefreshToken(refreshTokenData);
    setUsersRefreshTokenSessionStorage(data.data);
    return data.data;
  } catch (e) {
    console.error(e);
    authService.cleanStorage();
  }
};

/**
 * Asynchronously sends a forgot password request with the provided email.
 *
 * @param forgotPasswordData - The user's forgot password credentials.
 * @returns The response data from the API call, or an error message if the call fails.
 */
export const usersForgotPassword = createAsyncThunk(
  UsersActions.usersForgotPassword,
  async (forgotPasswordData: ForgotPasswordDto, { rejectWithValue }) => {
    try {
      const { data } = await UsersAPI.usersForgotPassword(forgotPasswordData);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error);
    }
  },
);

/**
 * Asynchronously creates a new password for the user with the provided set password data.
 *
 * @param setPasswordData - The user's set password credentials.
 * @returns The response data from the API call, or an error message if the call fails.
 */
export const usersCreateNewPassword = createAsyncThunk(
  UsersActions.usersCreateNewPassword,
  async (setPasswordData: UserSetPasswordDto, { rejectWithValue }) => {
    try {
      const { data } = await UsersAPI.usersCreateNewPassword(setPasswordData);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously verifies the user's email verification code with the provided email and code.
 *
 * @param usersVerifyCodeData - The user's email verification credentials.
 * @returns The response data from the API call, or an error message if the call fails.
 */
export const usersVerifyCode = createAsyncThunk(
  UsersActions.usersVerificationCode,
  async (
    usersVerifyCodeData: { email: string; code: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await UsersAPI.usersVerifyCode(usersVerifyCodeData);

      data ? dispatch(setIsValidCode(true)) : dispatch(setIsValidCode(false));
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously changes the user's password with the provided change password data.
 *
 * @param data - The user's change password credentials.
 * @returns The response data from the API call, or an error message if the call fails.
 */
export const userChangePassword = createAsyncThunk(
  UsersActions.userChangePassword,
  async (data: UserChangePasswordDto, { rejectWithValue }) => {
    try {
      const token = getToken();
      const { data: responseData } = await UsersAPI.userChangePassword(
        data,
        token,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously signs the user out.
 *
 * @returns The response data from the API call, or an error message if the call fails.
 */
export const signOut = createAsyncThunk(
  UsersActions.signOut,
  async (_, { rejectWithValue }) => {
    try {
      const { data: responseData } = await UsersAPI.signOut();
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously checks if the provided email already exists.
 *
 * @param email - The user's email address.
 * @returns The response data from the API call, or an error message if the call fails.
 */
export const checkUsersEmailExisting = createAsyncThunk(
  UsersActions.checkUsersEmailExisting,
  async (email: string, { rejectWithValue }) => {
    try {
      const { data } = await UsersAPI.checkUserEmailExisting(email);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously sets the user's preferred language.
 *
 * @param languageIso - The ISO code for the user's preferred language.
 * @returns The response data from the API call, or an error message if the call fails.
 */
export const setUserLanguage = createAsyncThunk(
  UsersActions.setUserLanguage,
  async (languageIso: string, { rejectWithValue }) => {
    try {
      const { data } = await UsersAPI.setUserLanguage(languageIso);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously retrieves the user's profile information.
 *
 * @returns The response data from the API call, or an error message if the call fails.
 */
export const getUserProfile = createAsyncThunk(
  UsersActions.getUserProfile,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await UsersAPI.getUserProfile();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously complete first login after registration.
 *
 */
export const completeFirstLogin = createAsyncThunk(
  UsersActions.completeFirstLogin,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await UsersAPI.completeFirstLogin();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
